import { useAuthStore } from './auth.store';
import type { IVerifyUser, IViewAsSession } from './auth.types';
import type { IProfile } from '@/types/profile';
import type { AccessPermissions } from '@/types/permission';

/**
 * A super Admin's "View as" session: a second, read-only token that stands in
 * for the Admin's own while it runs. Kept under its own key beside `auth`, so
 * the Admin token is never overwritten and comes back as soon as this goes.
 */
const VIEW_AS_STORAGE_KEY = 'view-as';

/** The API's answer when the simulated role cannot open a page. Not an exit. */
const NO_ACCESS_MESSAGE = 'You do not have permission to access this feature';

type StoredViewAs = {
  token: string;
  session: IViewAsSession;
  /** The Admin's own user, put back in the auth store on exit. */
  adminUser?: IVerifyUser;
};

const isStoredViewAs = (value: unknown): value is StoredViewAs =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as StoredViewAs).token === 'string' &&
  typeof (value as StoredViewAs).session?.expiresAt === 'string';

/**
 * Read straight from storage on every call, like the Admin token in
 * `setup-axios`: the axios interceptor and the route loaders run before any
 * store could rehydrate.
 */
const readViewAs = (): StoredViewAs | undefined => {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(VIEW_AS_STORAGE_KEY) ?? 'null'
    );
    return isStoredViewAs(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const saveViewAs = (viewAs: StoredViewAs) =>
  localStorage.setItem(VIEW_AS_STORAGE_KEY, JSON.stringify(viewAs));

const clearViewAs = () => localStorage.removeItem(VIEW_AS_STORAGE_KEY);

const isViewAsExpired = (session: IViewAsSession, now = Date.now()) =>
  now >= new Date(session.expiresAt).getTime();

/** True while this browser is viewing the CMS as another role. */
const isViewingAs = () => readViewAs() !== undefined;

/**
 * Drops the view-as token and puts the Admin's own user back, so the next
 * page load (its permission check reads the persisted user) is the Admin's
 * again. The caller reloads or navigates.
 */
const endViewAsLocally = () => {
  const adminUser = readViewAs()?.adminUser;
  clearViewAs();
  if (adminUser) useAuthStore.getState().setUser(adminUser);
};

/**
 * The user the CMS runs as from the moment a view-as starts until the next
 * page load replaces it with the profile: enough for the permission check
 * (role) and the company scope. Name and email stay the Admin's own.
 */
const viewAsUser = (
  session: IViewAsSession,
  roleId: string,
  adminUser: IVerifyUser | undefined
): IVerifyUser => ({
  id: adminUser?.id,
  name: adminUser?.name,
  email: adminUser?.email,
  role: { id: roleId, name: session.roleName },
  company: { id: session.companyId, name: session.companyName },
  branchUser: session.branchIds.map(id => ({ branch: { id } })),
});

/**
 * The role's permissions with nothing to create: a view-as is read-only, so
 * every create button that reads `canCreate` goes. Viewing, editing and
 * deleting stay the role's own, so its records still open as it sees them.
 */
const withoutCreate = (permissions: AccessPermissions): AccessPermissions =>
  Object.fromEntries(
    Object.entries(permissions).map(([module, access]) => [
      module,
      { ...access, canCreate: false },
    ])
  );

/** `GET /user/profile/me` in the shape the rest of the CMS reads as `user`. */
const profileToUser = (profile: IProfile): IVerifyUser => ({
  id: profile.id,
  name: profile.name,
  email: profile.email,
  role: profile.role,
  company: profile.company,
  branchUser: (profile.branch ?? []).map(({ id, name, address, status }) => ({
    branch: { id, name, address, status },
  })),
});

export type { StoredViewAs };
export {
  NO_ACCESS_MESSAGE,
  VIEW_AS_STORAGE_KEY,
  clearViewAs,
  endViewAsLocally,
  isViewAsExpired,
  isViewingAs,
  profileToUser,
  readViewAs,
  saveViewAs,
  viewAsUser,
  withoutCreate,
};
